'use client';

import { useLexicalComposerContext } from '@lexical/react/LexicalComposerContext';
import {
  FORMAT_TEXT_COMMAND,
  $getSelection,
  $isRangeSelection,
  $createParagraphNode,
  $getRoot,
  UNDO_COMMAND,
  REDO_COMMAND
} from 'lexical';
import { $createHeadingNode } from '@lexical/rich-text';
import {
  INSERT_UNORDERED_LIST_COMMAND,
  INSERT_ORDERED_LIST_COMMAND,
  REMOVE_LIST_COMMAND,
} from '@lexical/list';
import { TOGGLE_LINK_COMMAND } from '@lexical/link';
import { $createImageNode } from '../nodes/ImageNode';
import { $createColumnsNode } from '../nodes/ColumnsNode';
import { Button } from 'antd';
import UploadFile from '../../UploadFile';
import { API_ROUTE, SERVER_ROUTE } from '../../../utils/commonConstants';

export default function ToolbarPlugin({ initialState, disabled }) {
  const [editor] = useLexicalComposerContext();

  const applyHeading = (level) => {
    editor.update(() => {
      const sel = $getSelection();
      if (!$isRangeSelection(sel)) return;
      if (level === 0) {
        sel.insertNodes([$createParagraphNode()]);
        return;
      }
      sel.insertNodes([$createHeadingNode(('h' + level))]);
    });
  };

  const insertColumns = (cols) => {
    editor.update(() => {
      const node = $createColumnsNode(cols);
      node.append($createParagraphNode());
      node.append($createParagraphNode());
      if (cols === 3) node.append($createParagraphNode());
      $getSelection()?.insertNodes([node]);
    });
  };

  const insertImageByUrl = () => {
    const url = window.prompt('Image URL');
    if (!url) return;
    editor.update(() => {
      $getSelection()?.insertNodes([$createImageNode({ src: url })]);
    });
  };

  

  const onUndo = () => editor.dispatchCommand(UNDO_COMMAND, undefined);
  const onRedo = () => editor.dispatchCommand(REDO_COMMAND, undefined);

  const onClear = () => {
    if (!window.confirm('Clear all content? This cannot be undone.')) return;
    editor.update(() => {
      const root = $getRoot();
      root.clear();
      root.append($createParagraphNode());
      root.select();
    });
  };

  const onReset = () => {
    if (!initialState) return onClear();
    if (!window.confirm('Reset to the original content from when you opened this page?')) return;
    // safe to call in an event handler
    const state = editor.parseEditorState(initialState);
    editor.setEditorState(state);
  };

  const onUploadSuccess = (args) => {
    let url = args.url;
    if (!url) return;
    url = SERVER_ROUTE + url
    editor.update(() => {
      $getSelection()?.insertNodes([$createImageNode({ src: url })]);
    });
  }

  const deleteImage = (file) => {
    alert("Deleting image is not supported in editor yet. Please use the File Manager page to delete images.");
  }

  return (
    <div className="mb-2 flex flex-wrap items-center gap-2 rounded-xl border bg-background/55 p-2">
      {/* Text styles */}
      <Button size="sm" variant="secondary" type="primary" onClick={() => editor.dispatchCommand(FORMAT_TEXT_COMMAND, 'bold')}>Bold</Button>
      <Button size="sm" variant="secondary" type="primary" onClick={() => editor.dispatchCommand(FORMAT_TEXT_COMMAND, 'italic')}>Italic</Button>
      {/* <Button size="sm" variant="secondary" type="primary" onClick={() => editor.dispatchCommand(FORMAT_TEXT_COMMAND, 'underline')}>Underline</Button> */}

      <div className="mx-2 h-5 w-px bg-slate-200" />

      {/* Headings */}
      <Button size="sm" variant="secondary" type="primary" onClick={() => applyHeading(1)}>H1</Button>
      <Button size="sm" variant="secondary" type="primary" onClick={() => applyHeading(2)}>H2</Button>
      <Button size="sm" variant="secondary" type="primary" onClick={() => applyHeading(3)}>H3</Button>
      <Button size="sm" variant="ghost" type="primary" onClick={() => applyHeading(0)}>Para</Button>

      <div className="mx-2 h-5 w-px bg-slate-200" />

      {/* Lists & links */}
      <Button size="sm" variant="secondary" type="primary" onClick={() => editor.dispatchCommand(INSERT_UNORDERED_LIST_COMMAND, undefined)}>• List</Button>
      <Button size="sm" variant="secondary" type="primary" onClick={() => editor.dispatchCommand(INSERT_ORDERED_LIST_COMMAND, undefined)}>1. List</Button>
      <Button size="sm" variant="ghost" type="primary" onClick={() => editor.dispatchCommand(REMOVE_LIST_COMMAND, undefined)}>Unlist</Button>
      <Button size="sm" variant="secondary" type="primary" onClick={() => editor.dispatchCommand(TOGGLE_LINK_COMMAND, window.prompt('Link URL') || null)}>Link</Button>

      <div className="mx-2 h-5 w-px bg-slate-200" />

      {/* Layout & media */}
      <Button size="sm" type="primary" onClick={() => insertColumns(2)}>2 Col</Button>
      <Button size="sm" type="primary" onClick={() => insertColumns(3)}>3 Col</Button>
      <Button size="sm" type="primary" onClick={insertImageByUrl}>Image By URL</Button>

      <div className="mx-2 h-5 w-px bg-slate-200" />

      {/* History & clearing */}
      <Button size="sm" type='primary' onClick={onUndo}>Undo</Button>
      <Button size="sm" type="primary" onClick={onRedo}>Redo</Button>
      <Button size="sm" danger type="primary" onClick={onClear}>Clear</Button>
      <Button size="sm" danger type="primary" onClick={onReset}>Reset</Button>
      <div>
        <UploadFile disabled={disabled} onReload={() => {}} deleteImage={deleteImage} onUploadSuccess={onUploadSuccess}/>
      </div>
    </div>
  );
}
